import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  COLORS,
  DEFAULT_FONT_FAMILY,
  FONT_FAMILIES,
  formatFontSize,
  HIGHLIGHTS,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  selectionFontSize,
} from './extensions';
import { FontSizeField } from './FontSizeField';
import { LinkDialog } from './Toolbar';
import styles from './SelectionMenu.module.css';

const MARGIN = 8;

/** Is the point over the selected text? (right click elsewhere keeps the browser's own menu) */
function overSelection(editor: Editor, x: number, y: number): boolean {
  const { from, to, empty } = editor.state.selection;
  if (empty) return false;
  const hit = editor.view.posAtCoords({ left: x, top: y });
  return hit !== null && hit.pos >= from && hit.pos <= to;
}

/**
 * The context menu of a text selection in the note: right click on selected text opens it instead
 * of the browser's menu, with the formatting actions of the toolbar (the same editor commands).
 * Everywhere else - no selection, a click outside the selection, a note the person may not edit -
 * the browser's menu stays. The menu never takes the selection away: its buttons do not take focus
 * from the editor and every command restores it. Escape closes it; arrows move between actions.
 */
export function SelectionMenu({ editor }: { editor: Editor }) {
  const { t } = useTranslation();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const [linking, setLinking] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null);
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      superscript: e.isActive('superscript'),
      subscript: e.isActive('subscript'),
      link: e.isActive('link'),
      fontFamily:
        (e.getAttributes('textStyle') as { fontFamily?: string }).fontFamily?.replace(
          /["']/g,
          '',
        ) ?? DEFAULT_FONT_FAMILY,
      fontSize: selectionFontSize(e),
      align:
        (['left', 'center', 'right', 'justify'] as const).find((a) =>
          e.isActive({ textAlign: a }),
        ) ?? 'left',
    }),
  });
  const label = (key: string) => (t as unknown as (k: string) => string)(`documents.editor.${key}`);
  const close = () => {
    setAt(null);
    setPlaced(null);
  };
  const run = (command: (chain: ReturnType<Editor['chain']>) => void) => {
    command(editor.chain().focus());
    close();
  };

  useEffect(() => {
    let dom: HTMLElement | null = null;
    const open = (event: MouseEvent) => {
      if (!editor.isEditable || !overSelection(editor, event.clientX, event.clientY)) return;
      event.preventDefault();
      setPlaced(null);
      setAt({ x: event.clientX, y: event.clientY });
    };
    // The editor view only exists once `EditorContent` has mounted it: reading `editor.view` any
    // earlier throws and takes the whole note down. Attach the moment it is there.
    const attach = () => {
      if (dom) return;
      try {
        dom = editor.view.dom;
      } catch {
        return; // not mounted yet: the `mount` event calls this again
      }
      dom.addEventListener('contextmenu', open);
    };
    attach();
    editor.on('mount', attach);
    return () => {
      editor.off('mount', attach);
      dom?.removeEventListener('contextmenu', open);
    };
  }, [editor]);

  // keep the menu inside the viewport: measure it, then clamp
  useLayoutEffect(() => {
    if (!at || !menu.current) return;
    const box = menu.current.getBoundingClientRect();
    const left = Math.max(MARGIN, Math.min(at.x, window.innerWidth - box.width - MARGIN));
    const top = Math.max(MARGIN, Math.min(at.y, window.innerHeight - box.height - MARGIN));
    setPlaced({ left, top });
    menu.current.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  }, [at]);

  useEffect(() => {
    if (!at) return;
    const away = (event: Event) => {
      if (event.target instanceof Node && menu.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener('pointerdown', away, true);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [at]);

  if (linking) {
    return (
      <LinkDialog
        editor={editor}
        onClose={() => {
          setLinking(false);
        }}
      />
    );
  }
  if (!at) return null;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      editor.commands.focus();
      return;
    }
    if (event.key === 'Tab') {
      close();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (event.target instanceof HTMLInputElement) return; // the size field keeps its own keys
    const items = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [],
    );
    if (items.length === 0) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };

  const toggle = (
    name: string,
    testId: string,
    active: boolean,
    command: (chain: ReturnType<Editor['chain']>) => void,
  ) => (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={active}
      className={styles.item}
      data-testid={testId}
      onClick={() => {
        run(command);
      }}
    >
      {label(name)}
    </button>
  );

  return createPortal(
    <div
      ref={menu}
      role="menu"
      aria-label={label('selectionMenu')}
      className={styles.menu}
      data-testid="selection-menu"
      style={
        placed ? { left: placed.left, top: placed.top } : { left: at.x, top: at.y, opacity: 0 }
      }
      // taking focus on a mouse down would collapse the selection the menu is about
      onMouseDown={(event) => {
        if (!(
          event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement
        )) {
          event.preventDefault();
        }
      }}
      onContextMenu={(event) => {
        event.preventDefault();
      }}
      onKeyDown={onKeyDown}
    >
      <div className={styles.group} role="group" aria-label={label('groups.text')}>
        {toggle('bold', 'sel-bold', state.bold, (c) => c.toggleBold().run())}
        {toggle('italic', 'sel-italic', state.italic, (c) => c.toggleItalic().run())}
        {toggle('underline', 'sel-underline', state.underline, (c) => c.toggleUnderline().run())}
        {toggle('strike', 'sel-strike', state.strike, (c) => c.toggleStrike().run())}
        {toggle('superscript', 'sel-superscript', state.superscript, (c) =>
          c.toggleMark('superscript').run(),
        )}
        {toggle('subscript', 'sel-subscript', state.subscript, (c) =>
          c.toggleMark('subscript').run(),
        )}
      </div>
      <div className={styles.group} role="group" aria-label={label('format')}>
        <select
          className={styles.select}
          aria-label={label('fontFamily')}
          value={state.fontFamily}
          data-testid="sel-font-family"
          onChange={(event) => {
            run((c) => c.setFontFamily(event.target.value).run());
          }}
        >
          {FONT_FAMILIES.map((family) => (
            <option key={family} value={family}>
              {family}
            </option>
          ))}
        </select>
        <FontSizeField
          value={state.fontSize}
          label={label('fontSize')}
          mixedLabel={label('fontSizeMixed')}
          invalidLabel={(t as unknown as (k: string, o: object) => string)(
            'documents.editor.fontSizeInvalid',
            { min: MIN_FONT_SIZE, max: MAX_FONT_SIZE },
          )}
          onApply={(pt) => {
            run((c) => c.setFontSize(`${formatFontSize(pt)}pt`).run());
          }}
        />
      </div>
      <div className={styles.group} role="group" aria-label={label('color')}>
        {COLORS.map((color) => (
          <button
            key={color}
            type="button"
            role="menuitem"
            className={styles.swatch}
            style={{ background: color }}
            aria-label={`${label('color')} ${color}`}
            data-testid={`sel-color-${color}`}
            onClick={() => {
              run((c) => c.setColor(color).run());
            }}
          />
        ))}
        <button
          type="button"
          role="menuitem"
          className={styles.item}
          data-testid="sel-color-reset"
          onClick={() => {
            run((c) => c.unsetColor().run());
          }}
        >
          {label('colorReset')}
        </button>
      </div>
      <div className={styles.group} role="group" aria-label={label('highlight')}>
        {HIGHLIGHTS.map((color) => (
          <button
            key={color}
            type="button"
            role="menuitem"
            className={styles.swatch}
            style={{ background: color }}
            aria-label={`${label('highlight')} ${color}`}
            data-testid={`sel-highlight-${color}`}
            onClick={() => {
              run((c) => c.setHighlight({ color }).run());
            }}
          />
        ))}
        <button
          type="button"
          role="menuitem"
          className={styles.item}
          data-testid="sel-highlight-reset"
          onClick={() => {
            run((c) => c.unsetHighlight().run());
          }}
        >
          {label('highlightReset')}
        </button>
      </div>
      <div className={styles.group} role="group" aria-label={label('alignment')}>
        {(['left', 'center', 'right', 'justify'] as const).map((align) => (
          <button
            key={align}
            type="button"
            role="menuitemradio"
            aria-checked={state.align === align}
            className={styles.item}
            data-testid={`sel-align-${align}`}
            onClick={() => {
              run((c) => c.setTextAlign(align).run());
            }}
          >
            {label(`align.${align}`)}
          </button>
        ))}
      </div>
      <div className={styles.group}>
        <button
          type="button"
          role="menuitem"
          className={styles.item}
          data-testid="sel-link"
          onClick={() => {
            close();
            setLinking(true);
          }}
        >
          {label('link')}
        </button>
        <button
          type="button"
          role="menuitem"
          className={styles.item}
          data-testid="sel-clear"
          onClick={() => {
            run((c) => c.unsetAllMarks().clearNodes().run());
          }}
        >
          {label('clearFormatting')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
